import React, { useState } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { CAN_ID_STRING_LENGTH } from 'utils/constants'
import { compose } from 'ramda'
import { classNames } from 'utils/utils'
import { SelectMenu } from 'components/chec'
import {
  isNonAdditiveValidKeyCombo,
  isHexadecimalChar,
  isEightHexadecimalChars,
  parseHexadecimal,
} from 'utils/keyboardUtils'
import withStyle, { AddButtonStyles } from './CanIdEditor.style.js'

const CanIdEditor = ({ className, canId, featureCanIds, setCanId }) => {
  const parsedList = featureCanIds.map(parseHexadecimal)
  const isValidCanId = (id) =>
    id.match(isEightHexadecimalChars) &&
    !parsedList.includes(parseHexadecimal(id))

  const options = featureCanIds.map((i) => ({
    key: i,
    value: i,
    title: i,
  }))

  // controls the add button's style and when it's clickable:
  const [canMakeNewId, setCanMakeNewId] = useState(false)

  // antd's select only provides the value of the typed search input in events that are
  // raised on that input, but we need that value in the context of an event raised outside
  // of the input (the "add" button attached to the dropdown), so we're tracking
  // the typed value it with this variable:
  const [userTypedInput, setUserTypedInput] = useState('')

  const clearSearch = () => {
    setUserTypedInput('')
    setCanMakeNewId(false)
  }

  const handleUpdatedSearchText = (chars) => {
    setCanMakeNewId(isValidCanId(chars))
    setUserTypedInput(chars)
  }

  const maybeAddCanId = () => {
    if (!canMakeNewId) {
      return
    }
    setCanId(userTypedInput)
    clearSearch()
  }

  const preventInvalidTyping = (e) => {
    const { ctrlKey, key } = e
    if (
      isHexadecimalChar(key) &&
      userTypedInput.length < CAN_ID_STRING_LENGTH
    ) {
      return
    }
    if (isNonAdditiveValidKeyCombo(ctrlKey, key)) {
      return
    }
    e.preventDefault()
  }

  return (
    <SelectMenu
      vertical
      label={strings.can_id_hex_16}
      onInputKeyDown={preventInvalidTyping}
      onSearch={handleUpdatedSearchText}
      value={canId}
      showArrow={false}
      className={classNames(className, 'can-id-editor')}
      options={options}
      showSearch={true}
      filterOption
      open={true}
      onChange={setCanId}
      onBlur={clearSearch}
      dropdownRender={(menu) => (
        <>
          {menu}
          <AddButtonStyles canMakeNewId={canMakeNewId}>
            <div
              onMouseDown={maybeAddCanId} // antd issue requires attaching to onMouseDown instead of onClick
              className={
                canMakeNewId ? 'new-can-id-button-checked' : 'new-can-id-button'
              }
              data-testid="define-new-can-id-link"
            >
              {strings.define_new_can_id}
            </div>
          </AddButtonStyles>
        </>
      )}
      data-testid="can-id-select"
    />
  )
}

CanIdEditor.defaultProps = {
  featureCanIds: [],
}

CanIdEditor.propTypes = {
  /** Make sure to comment */
  /** Classname */
  className: PropTypes.string,
  /** The can id */
  canId: PropTypes.string,
  /** an array of ids available for the feature */
  featureCanIds: PropTypes.arrayOf(PropTypes.string),
  /** a handler for changes of a selected can id. */
  setCanId: PropTypes.func,
}

export default compose(withStyle)(CanIdEditor)
